import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicStandings } from '@/lib/api';
import { TvQrCode } from '@/components/TvQrCode';
import type { StandingsResponse, TiebreakCriterion } from '@swiss-magic/shared-types';
import { formatTiebreak } from '@/lib/tiebreak-labels';

const CYCLE_MS = 30_000;

export function TvStandingsPage() {
  const { t } = useTranslation('tournament');
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<StandingsResponse | null>(null);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Fetch data with polling
  useEffect(() => {
    if (!slug) return;
    const load = () => getPublicStandings(slug).then(setData).catch(() => {});
    load();
    const interval = setInterval(load, 5_000);
    return () => clearInterval(interval);
  }, [slug]);

  // Measure actual row height from DOM
  const measureRows = useCallback(() => {
    if (!containerRef.current) return;
    const tableWrapper = containerRef.current.querySelector('.overflow-hidden');
    const firstRow = tableRef.current?.querySelector('tr');
    if (!tableWrapper || !firstRow) return;
    const available = tableWrapper.clientHeight;
    const rowHeight = firstRow.getBoundingClientRect().height || 36;
    const theadHeight = containerRef.current.querySelector('thead')?.getBoundingClientRect().height || 32;
    setRowsPerPage(Math.max(5, Math.floor((available - theadHeight) / rowHeight)));
  }, []);

  useEffect(() => {
    const t1 = setTimeout(measureRows, 100);
    const t2 = setTimeout(measureRows, 500);
    window.addEventListener('resize', measureRows);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', measureRows); };
  }, [measureRows, data]);

  const totalRef = useRef(0);
  useEffect(() => {
    totalRef.current = data?.standings.length ?? 0;
  }, [data]);

  // Auto-cycle — depends only on rowsPerPage (not data, to avoid resetting timer on poll)
  useEffect(() => {
    if (rowsPerPage >= 100) return;
    const timer = setInterval(() => {
      const total = totalRef.current;
      if (total <= rowsPerPage) return;
      setOffset((prev) => {
        const next = prev + rowsPerPage;
        return next >= total ? 0 : next;
      });
    }, CYCLE_MS);
    return () => clearInterval(timer);
  }, [rowsPerPage]);

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-lg text-muted-foreground">{t('loading')}</p>
      </div>
    );
  }

  const visibleRows = data.standings.slice(offset, offset + rowsPerPage);
  const totalPages = Math.ceil(data.standings.length / rowsPerPage);
  const currentPage = Math.floor(offset / rowsPerPage) + 1;

  return (
    <div ref={containerRef} data-testid="tv-standings-page" className="h-screen bg-background p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold">{data.tournamentName}</h1>
          <p className="text-sm text-muted-foreground">{t('standings.subtitle')}</p>
        </div>
        {totalPages > 1 && (
          <span className="text-sm text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-hidden rounded border">
        <table className="w-full text-left">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="w-10 px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('standings.table.rank')}</th>
              <th className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('standings.table.name')}</th>
              <th className="w-14 px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('standings.table.points')}</th>
              <th className="w-12 px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('standings.table.games_played')}</th>
              {data.tiebreakCriteria.map((c) => (
                <th key={c} className="w-16 px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={tableRef}>
            {visibleRows.map((row) => {
              const name = `${row.firstName} ${row.lastName}`;
              const isOut = row.status === 'eliminated' || row.status === 'withdrawn';
              return (
                <tr key={row.startingRank} className="border-b">
                  <td className="px-2 py-1.5 text-sm">
                    {isOut && <span className="mr-1 text-muted-foreground">✕</span>}
                    {row.rank}
                  </td>
                  <td className={`px-2 py-1.5 text-sm ${isOut ? 'text-muted-foreground' : 'font-medium'}`}>
                    {name}
                  </td>
                  <td className="px-2 py-1.5 text-sm font-semibold">{row.points}</td>
                  <td className="px-2 py-1.5 text-sm text-muted-foreground">{row.gamesPlayed}</td>
                  {data.tiebreakCriteria.map((c) => (
                    <td key={c} className="px-2 py-1.5 text-sm">
                      {formatTiebreak(c as TiebreakCriterion, row.tiebreaks[c] ?? 0)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {slug && <TvQrCode slug={slug} reportPath="standings" />}
    </div>
  );
}
